import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Download as DownloadIcon,
  AutoAwesome as AutoAwesomeIcon,
} from '@mui/icons-material';
import { useTranslation } from '../hooks/useTranslation';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useAmplitude } from '../hooks/useAmplitude';
import api from '../utils/api';

export const PromptToImage: React.FC = () => {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { track } = useAmplitude();
  const isRTL = currentLang === 'he';

  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [taskStatus, setTaskStatus] = useState<'CREATED' | 'IN_PROGRESS' | 'COMPLETED' | 'FAILED' | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Poll for task status
  const pollTaskStatus = async (taskIdToPoll: string) => {
    try {
      console.log('Polling task status for:', taskIdToPoll);
      // Add cache-busting parameter to prevent 304 responses
      const response = await api.get(`/generated-images/task/${taskIdToPoll}`, {
        params: {
          _t: Date.now(), // Timestamp to prevent caching
        },
      });
      
      if (response.data.success) {
        const { status, imageBase64, error: taskError } = response.data.data;
        console.log('Task status:', status);
        setTaskStatus(status);

        if (status === 'COMPLETED' && imageBase64) {
          // Stop polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setGeneratedImage(imageBase64);
          setLoading(false);
          
          // Track generation event
          track('Prompt to Image - Generated', {
            prompt_length: prompt.length,
            has_image: !!imageBase64,
            task_id: taskIdToPoll,
          });
        } else if (status === 'FAILED') {
          // Stop polling
          if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
          }
          setLoading(false);
          setError(taskError || t('pages.freeTools.promptToImage.errors.generationFailed'));
        }
        // If status is CREATED or IN_PROGRESS, continue polling
      }
    } catch (err: any) {
      console.error('Error polling task status:', err);
      // Don't stop polling on error, might be temporary
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
    };
  }, []);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError(t('pages.freeTools.promptToImage.errors.emptyPrompt'));
      return;
    }

    if (!isAuthenticated) {
      setError(t('pages.freeTools.promptToImage.errors.loginRequired'));
      setTimeout(() => {
        navigate('/login', { state: { from: '/free-tools' } });
      }, 2000);
      return;
    }

    setLoading(true);
    setError(null);
    setGeneratedImage(null);
    setTaskStatus(null);

    // Clear any existing polling
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    try {
      const response = await api.post('/generated-images/generate', {
        prompt: prompt.trim(),
        negativePrompt: negativePrompt.trim() || undefined,
      });

      if (response.data.success) {
        const { taskId: newTaskId, status } = response.data.data;
        console.log('Task created:', newTaskId, 'Status:', status);
        setTaskId(newTaskId);
        setTaskStatus(status);
        
        // Start polling for status
        pollingIntervalRef.current = setInterval(() => {
          pollTaskStatus(newTaskId);
        }, 10000); // Poll every 10 seconds
        
        // Also poll immediately
        pollTaskStatus(newTaskId);
      } else {
        throw new Error(response.data.error || 'Failed to generate image');
      }
    } catch (err: any) {
      console.error('Error generating image:', err);
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        t('pages.freeTools.promptToImage.errors.generationFailed');
      setError(errorMessage);
      setLoading(false);
    }
  };

  const handleDownloadPNG = async () => {
    if (!generatedImage) return;

    try {
      // Convert base64 to blob (handle both with and without data URI prefix)
      let base64Data = generatedImage;
      if (base64Data.includes(',')) {
        base64Data = base64Data.split(',')[1];
      }
      
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/png' });

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `cricut-image-${Date.now()}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      // Track download event
      track('Prompt to Image - Download PNG', {
        task_id: taskId,
      });
    } catch (err) {
      console.error('Error downloading PNG:', err);
      setError(t('pages.freeTools.promptToImage.errors.downloadFailed'));
    }
  };

  const handleDownloadSVG = async () => {
    if (!generatedImage) return;

    try {
      // Convert base64 image to SVG
      // First, create an image element from the base64
      const img = new Image();
      img.crossOrigin = 'anonymous';
      
      // Ensure base64 has data URI prefix for image src
      let imageSrc = generatedImage;
      if (!imageSrc.startsWith('data:')) {
        imageSrc = `data:image/png;base64,${imageSrc}`;
      }
      
      await new Promise<void>((resolve, reject) => {
        img.onload = () => {
          // Convert to SVG by embedding the image
          const svgContent = `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${img.width}" height="${img.height}" viewBox="0 0 ${img.width} ${img.height}">
  <image width="${img.width}" height="${img.height}" xlink:href="${imageSrc}"/>
</svg>`;

          // Create download link
          const blob = new Blob([svgContent], { type: 'image/svg+xml' });
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `cricut-image-${Date.now()}.svg`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          resolve();
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = imageSrc;
      });

      // Track download event
      track('Prompt to Image - Download SVG', {
        task_id: taskId,
      });
    } catch (err) {
      console.error('Error downloading SVG:', err);
      setError(t('pages.freeTools.promptToImage.errors.downloadFailed'));
    }
  };

  return (
    <Box sx={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ textAlign: isRTL ? 'right' : 'left' }}>
          {t('pages.freeTools.promptToImage.title')}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 3,
            textAlign: isRTL ? 'right' : 'left',
            whiteSpace: 'pre-line',
          }}
        >
          {t('pages.freeTools.promptToImage.description')}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder={t('pages.freeTools.promptToImage.promptPlaceholder')}
            disabled={loading}
            sx={{
              mb: 2,
              '& .MuiInputBase-input': {
                direction: 'rtl',
                textAlign: 'right',
              },
            }}
          />
          <TextField
            fullWidth
            multiline
            rows={2}
            value={negativePrompt}
            onChange={(e) => setNegativePrompt(e.target.value)}
            placeholder={t('pages.freeTools.promptToImage.negativePromptPlaceholder')}
            disabled={loading}
            sx={{
              mb: 2,
              '& .MuiInputBase-input': {
                direction: 'rtl',
                textAlign: 'right',
              },
            }}
          />
          <Button
            variant="contained"
            size="large"
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
            onClick={handleGenerate}
            disabled={loading || !prompt.trim()}
            sx={{ gap: 1 }}
          >
            {loading ? t('pages.freeTools.promptToImage.generating') : t('pages.freeTools.promptToImage.generateButton')}
          </Button>
          {loading && taskStatus && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: isRTL ? 'right' : 'left' }}>
              {taskStatus === 'CREATED' && 'Task created...'}
              {taskStatus === 'IN_PROGRESS' && 'Generating image...'}
            </Typography>
          )}
        </Box>

        {generatedImage && (
          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" gutterBottom sx={{ textAlign: isRTL ? 'right' : 'left', mb: 2 }}>
                {t('pages.freeTools.promptToImage.success')}
              </Typography>
              <Box
                sx={{
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  p: 2,
                  backgroundColor: '#fff',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  mb: 2,
                  minHeight: '300px',
                }}
              >
                <img
                  src={generatedImage.startsWith('data:') ? generatedImage : `data:image/png;base64,${generatedImage}`}
                  alt="Generated"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    maxHeight: '500px',
                  }}
                />
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadSVG}
                  sx={{ gap: 1 }}
                >
                  {t('pages.freeTools.promptToImage.downloadSvg')}
                </Button>
                <Button
                  variant="contained"
                  size="medium"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadPNG}
                  sx={{ gap: 1 }}
                >
                  {t('pages.freeTools.promptToImage.downloadPng')}
                </Button>
              </Box>
            </CardContent>
          </Card>
        )}
      </Paper>
    </Box>
  );
};

